/**
 * Top navigation header — persistent across all routes via
 * {@link ../../app.component.ts}.
 *
 * Owns the mobile menu open/close lifecycle. Two host bindings drive
 * dismissal: pressing Escape closes the menu, and a document-level
 * click handler closes it when the click lands outside the
 * `.mobile-dropdown-wrapper` element. The CLAUDE.md rule against
 * `@HostBinding`/`@HostListener` is honored by using the `host`
 * metadata block instead.
 */
import { Component, ElementRef, inject, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { RouterModule } from '@angular/router';

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [CommonModule, MatToolbarModule, MatButtonModule, MatIconModule, RouterModule],
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  host: {
    '(document:keydown.escape)': 'onEscapeKey()',
    '(document:click)': 'onDocumentClick($event)',
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HeaderComponent {
  private elementRef = inject(ElementRef);
  mobileMenuOpen = false;

  onEscapeKey(): void {
    if (this.mobileMenuOpen) {
      this.closeMobileMenu();
    }
  }

  onDocumentClick(event: MouseEvent): void {
    if (this.mobileMenuOpen) {
      const wrapper = this.elementRef.nativeElement.querySelector('.mobile-dropdown-wrapper');
      if (wrapper && !wrapper.contains(event.target as Node)) {
        this.closeMobileMenu();
      }
    }
  }

  toggleMobileMenu(): void {
    this.mobileMenuOpen = !this.mobileMenuOpen;
  }

  closeMobileMenu(): void {
    this.mobileMenuOpen = false;
  }
}
